import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { TariffQuestion, getRandomQuestion } from '../data/tariffQuestions';

interface GameState {
  creditScore: number;
  answeredQuestions: number;
  isGameOver: boolean;
}

interface GameContextType {
  creditScore: number; // This represents the tariff score in the game
  currentQuestion: TariffQuestion | null;
  answeredQuestions: number;
  isGameOver: boolean;
  showFeedback: boolean;
  isAnswerCorrect: boolean | null;
  walletConnected: boolean;
  walletAddress: string | null;
  setWalletConnected: (connected: boolean, address?: string | null) => void;
  answerQuestion: (answer: boolean) => void;
  resetGame: () => void;
  nextQuestion: () => void;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

interface GameProviderProps {
  children: ReactNode;
}

export const GameProvider: React.FC<GameProviderProps> = ({ children }) => {
  const [creditScore, setCreditScore] = useState<number>(100); // This represents the tariff score
  const [currentQuestion, setCurrentQuestion] = useState<TariffQuestion | null>(null);
  const [answeredQuestions, setAnsweredQuestions] = useState<number>(0);
  const [isGameOver, setIsGameOver] = useState<boolean>(false);
  const [showFeedback, setShowFeedback] = useState<boolean>(false);
  const [isAnswerCorrect, setIsAnswerCorrect] = useState<boolean | null>(null);
  const [walletConnected, setWalletConnected] = useState<boolean>(false);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [previousWalletState, setPreviousWalletState] = useState<boolean>(false);
  const [previousWalletAddress, setPreviousWalletAddress] = useState<string | null>(null);

  // Save game state to localStorage for specific wallet
  const saveGameState = (address: string | null) => {
    if (!address) return;
    
    const gameState: GameState = {
      creditScore,
      answeredQuestions,
      isGameOver,
    };
    
    try {
      localStorage.setItem(`tariffGame_${address}`, JSON.stringify(gameState));
    } catch (error) {
      console.error('Error saving game state:', error);
    }
  };

  // Load game state from localStorage for specific wallet
  const loadGameState = (address: string | null): GameState | null => {
    if (!address) return null;
    
    try {
      const savedState = localStorage.getItem(`tariffGame_${address}`);
      if (savedState) {
        return JSON.parse(savedState) as GameState;
      }
    } catch (error) {
      console.error('Error loading game state:', error);
    }
    
    return null;
  };

  // Handle wallet connection/disconnection
  const handleWalletConnection = (connected: boolean, address: string | null = null) => {
    setWalletConnected(connected);
    setWalletAddress(address);
  };

  useEffect(() => {
    if (!currentQuestion && !isGameOver && !showFeedback) {
      setCurrentQuestion(getRandomQuestion());
    }
  }, [currentQuestion, isGameOver, showFeedback]);

  useEffect(() => {
    if (creditScore >= 1000) {
      setIsGameOver(true);
    }
  }, [creditScore]);

  // Handle wallet address changes
  useEffect(() => {
    // If the wallet address has changed
    if (walletAddress !== previousWalletAddress) {
      // If we have a new wallet address
      if (walletAddress) {
        // Load game state for this wallet (if exists)
        const savedState = loadGameState(walletAddress);
        
        if (savedState) {
          // Restore saved state
          setCreditScore(savedState.creditScore);
          setAnsweredQuestions(savedState.answeredQuestions);
          setIsGameOver(savedState.isGameOver);
          setCurrentQuestion(getRandomQuestion());
          setShowFeedback(false);
          setIsAnswerCorrect(null);
        } else {
          // No saved state, initialize new game
          resetGame();
        }
      }
      
      // Update previous wallet address
      setPreviousWalletAddress(walletAddress);
    }
  }, [walletAddress]);

  // Save state on important state changes
  useEffect(() => {
    if (walletAddress) {
      saveGameState(walletAddress);
    }
  }, [creditScore, answeredQuestions, isGameOver, walletAddress]);

  // Reset game state when wallet connection changes
  useEffect(() => {
    // Handle disconnect (reset UI state but not game progress)
    if (!walletConnected && previousWalletState) {
      setShowFeedback(false);
      setIsAnswerCorrect(null);
    }
    
    setPreviousWalletState(walletConnected);
  }, [walletConnected]);

  const calculateScoreChange = (isCorrect: boolean): number => {
    if (isCorrect) {
      return Math.floor(Math.random() * 16) + 5; // Random between 5 and 20 for tariff score increase
    } else {
      return -1 * (Math.floor(Math.random() * 16) + 5); // Random between -5 and -20 for tariff score decrease
    }
  };

  const answerQuestion = (answer: boolean) => {
    if (!currentQuestion || showFeedback) return;

    const isCorrect = answer === currentQuestion.isGood;
    const scoreChange = calculateScoreChange(isCorrect);
    
    setIsAnswerCorrect(isCorrect);
    setCreditScore(prev => Math.max(0, Math.min(1000, prev + scoreChange)));
    setShowFeedback(true);
    setAnsweredQuestions(prev => prev + 1);
  };

  const nextQuestion = () => {
    setCurrentQuestion(getRandomQuestion());
    setShowFeedback(false);
    setIsAnswerCorrect(null);
  };

  const resetGame = () => {
    setCreditScore(100);
    setCurrentQuestion(getRandomQuestion());
    setAnsweredQuestions(0);
    setIsGameOver(false);
    setShowFeedback(false);
    setIsAnswerCorrect(null);
  };

  const value = {
    creditScore,
    currentQuestion,
    answeredQuestions,
    isGameOver,
    showFeedback,
    isAnswerCorrect,
    walletConnected,
    walletAddress,
    setWalletConnected: handleWalletConnection,
    answerQuestion,
    resetGame,
    nextQuestion
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};

export const useGame = (): GameContextType => {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}; 